import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase, IDBPObjectStore, StoreNames } from 'idb';
import type { Position, Detail } from '../types';

// ── Constantes de la cola offline (paridad Android) ─────────────────────────
// MAX_RETRIES = 100 (GeneralWork.kt). La primera versión copió el tope de
// Android (20) sin copiar su RITMO: allí cada reintento cuesta minutos (worker
// espaciado + backoff de WorkManager), aquí el poll busy reintentaba cada
// ~1,5 s y el tope se quemaba en medio minuto de mala suerte. El tope solo
// significa lo mismo acompañado del backoff exponencial por item
// (nextAttemptAt) — con él, 100 reintentos vuelven a ser horas de fallos
// REALES.
//
// NO hay descarte por antigüedad: una carga con fotos de hace 8 días sigue
// siendo trabajo del controller que debe llegar al portal. El antiguo corte a
// los 7 días era un vector de pérdida silenciosa (la app Android tampoco
// descarta, solo avisa).
export const MAX_RETRIES = 100;

export type OperateType = 'NEW' | 'EDIT' | 'DELETE';

export interface PendingDetail {
  id: number;
  idCargo: number;
  // Para NEW, idCargoDetail será 0 hasta que se sincronice. Lo usamos como índice
  // en EDIT/DELETE para encontrar y deduplicar operaciones sobre el mismo detalle.
  idCargoDetail: number;
  operateType: OperateType;
  detail: Detail;
  retries: number;
  createdAt: number;
  // Marca temporal del último intento (para back-off en cliente).
  lastAttemptAt?: number;
  // Backoff exponencial POR ITEM: hasta este instante las pasadas SALTAN el
  // item (sin consumir retry ni lease). Se fija en cada bump como
  // now + min(5s × 2^retries, 5min); lo limpian el primer éxito real del item
  // (dato entregado / foto confirmada), el reset de retries y "Forzar subida".
  // Es lo que da al tope de reintentos el mismo RITMO que Android.
  nextAttemptAt?: number;
  // DATOS del item ya entregados al servidor (pasada 1 del flush
  // datos-primero); solo quedan fotos. Evita re-POSTear los datos en cada
  // pasada. Se limpia si el usuario vuelve a editar el pending — los datos
  // nuevos deben viajar otra vez.
  dataSyncedAt?: number;
  // Nº de 404 CONSECUTIVOS al editar (detalle borrado en el portal). A partir
  // de 3, el item entra en back-off (un intento cada 15 min) en vez del bucle
  // frío continuo. Se limpia con el primer EDIT aceptado.
  notFound404s?: number;
  // Destino CONFIRMADO inexistente en el portal (ficha borrada entera). Se
  // marca solo tras leer el cargo y ver que el detalle ya no está — nunca por
  // un fallo de red. Con este flag el item deja de reintentarse en ambas colas
  // (se salta a nivel de bucle) pero permanece VISIBLE con sus fotos
  // conservadas; el usuario recibe un aviso. "Forzar subida" lo limpia (por si
  // el detalle reapareciera).
  targetGoneAt?: number;
  // Rechazos CONSECUTIVOS del servidor (respuesta real 5xx al POST de datos).
  // No cuenta red caída, timeouts ni cancelaciones. A partir de 3 la capa de
  // UI avisa al controlador con el contenedor y qué hacer — sin esto un item
  // que el servidor rechaza siempre se ve como "1 pendiente" para siempre y
  // nadie sabe por qué (4 sep 2026, Egipto). Se limpia con el primer POST de
  // datos aceptado. El texto es lo que respondió la API (recortado).
  serverRejects?: number;
  serverRejectMsg?: string;
  // ── Lease de procesado cross-contexto ──
  // Dos contextos procesan esta cola (flush del cliente y sync del SW) y
  // tryFlush los dispara A LA VEZ. El lease marca quién está procesando el
  // item y hasta cuándo; el otro contexto lo salta mientras esté vigente.
  // Caducado se puede robar: si el dueño murió a mitad, el item no queda
  // huérfano.
  leaseOwner?: string;
  leasedUntil?: number;
}

interface AldahraDB extends DBSchema {
  positions: {
    key: string;
    value: { date: string; data: Position[] };
  };
  pendingDetails: {
    key: number;
    value: PendingDetail;
    indexes: {
      'by-cargo': number;
      'by-detail': number;
    };
    autoIncrement: true;
  };
  cargoDetails: {
    key: number;
    value: { idCargo: number; data: Detail[]; cachedAt: number };
  };
  authToken: {
    key: string;
    value: string;
  };
  factoryGeocodes: {
    key: string;  // factoryName normalizado
    value: {
      factoryName: string;     // clave (tal como viene en BD, sin normalizar)
      lat: number | null;      // null = miss (no se pudo geocodificar)
      lng: number | null;
      country?: string | null; // opcional, para tooltip
      geocodedAt: number;      // ms
    };
  };
  // v5: borradores de fotos por carga/detalle. Las fotos quedan aquí EN EL
  // MOMENTO de añadirlas (no al guardar) — si la pestaña muere, reaparecen al
  // reabrir esa misma carga.
  photoDrafts: {
    key: string;
    value: PhotoDraft;
  };
  // v6: documento de transporte (DDT) del proveedor pendiente de subir o de
  // quitar (2026-10-23). Uno por contenedor: lo último que se pidió manda.
  deliveryNoteQueue: {
    key: number;
    value: QueuedDeliveryNote;
  };
}

let _db: IDBPDatabase<AldahraDB> | null = null;
let _dbPromise: Promise<IDBPDatabase<AldahraDB>> | null = null;

// Invalida el handle cacheado. Lo llamamos cuando el navegador cierra la
// conexión por su cuenta (freeze/bfcache en iOS, otra pestaña que hace un
// upgrade de versión, etc.). Sin esto, el `_db` muerto se quedaba cacheado y
// TODAS las operaciones posteriores lanzaban InvalidStateError, atascando la
// sincronización offline hasta recargar la app.
function resetDB(): void {
  _db = null;
  _dbPromise = null;
}

async function openDatabase(): Promise<IDBPDatabase<AldahraDB>> {
  const db = await openDB<AldahraDB>('aldahra-db', 6, {
    upgrade(db, oldVersion, _newVersion, tx) {
      // WebKit (Safari / WKWebView embebido en otra app) puede disparar
      // upgradeneeded con request.result nulo cuando la apertura ya está
      // abortada —almacenamiento restringido o página saliendo—. Sin esta
      // guarda, `db.createObjectStore` reventaba con un TypeError no capturado
      // (Sentry PWA-13, iPhone iOS 18.7, 21 sep 2026). Al salir sin tocar
      // nada, idb cierra la apertura con AbortError, que sí recogen los
      // llamadores y getDB trata como "sin almacenamiento por ahora".
      if (!db) return;
      if (oldVersion < 1) {
        db.createObjectStore('positions', { keyPath: 'date' });
        db.createObjectStore('pendingDetails', { autoIncrement: true, keyPath: 'id' });
      }
      if (oldVersion < 2) {
        db.createObjectStore('cargoDetails', { keyPath: 'idCargo' });
        db.createObjectStore('authToken');
      }
      if (oldVersion < 3) {
        // Añadir índices a la cola para queries rápidas por cargo/detalle.
        // Si la store ya existe (oldVersion >= 1) accedemos vía la transacción
        // del upgrade; si no, la creamos con sus índices.
        const store = db.objectStoreNames.contains('pendingDetails')
          ? tx.objectStore('pendingDetails')
          : db.createObjectStore('pendingDetails', { autoIncrement: true, keyPath: 'id' });
        if (!store.indexNames.contains('by-cargo')) {
          store.createIndex('by-cargo', 'idCargo');
        }
        if (!store.indexNames.contains('by-detail')) {
          store.createIndex('by-detail', 'idCargoDetail');
        }
      }
      if (oldVersion < 4) {
        // Cache persistente de geocoding de fábricas (tanto hits como
        // misses). Clave = factoryName normalizado lowercase trim. Permite
        // que el mapa pinte instantáneo en visitas posteriores.
        db.createObjectStore('factoryGeocodes', { keyPath: 'factoryName' });
      }
      if (oldVersion < 5) {
        // Borradores de fotos por carga/detalle (persistencia al capturar).
        db.createObjectStore('photoDrafts', { keyPath: 'key' });
      }
      if (oldVersion < 6 && !db.objectStoreNames.contains('deliveryNoteQueue')) {
        // Cola del DDT del proveedor, aparte de la de detalles y fotos.
        db.createObjectStore('deliveryNoteQueue', { keyPath: 'idCargoDetail' });
      }
    },
    blocking() {
      // Otra pestaña quiere abrir una versión más nueva y NOSOTROS la estamos
      // bloqueando: cerramos para no atascar el upgrade y forzamos reapertura.
      db.close();
      resetDB();
    },
    terminated() {
      // El navegador cerró la conexión de forma anómala (típico en iOS al
      // congelar/reactivar la pestaña). El handle ya no sirve.
      resetDB();
    },
  });
  // Red de seguridad adicional: si la conexión se cierra por cualquier vía,
  // purgamos el cache para que el siguiente getDB() reabra una conexión viva.
  db.addEventListener('close', () => {
    if (_db === db) resetDB();
  });
  return db;
}

// Apertura fallida: cuándo y con qué. Mientras dure la espera, getDB rechaza
// con el MISMO error sin volver a abrir, y ese error va marcado como ya
// reportado: sin esto, un dispositivo sin IndexedDB (WKWebView con
// almacenamiento restringido, modo privado) reintentaba la apertura en cada
// tick del sync (cada 1,5 s con cola) y mandaba un evento a Sentry por tick.
const OPEN_RETRY_MS = 60_000;
let _openFailedAt = 0;
let _openError: unknown = null;

async function getDB(): Promise<IDBPDatabase<AldahraDB>> {
  if (_db) return _db;
  if (_openError && Date.now() - _openFailedAt < OPEN_RETRY_MS) {
    throw _openError;
  }
  if (!_dbPromise) {
    _dbPromise = openDatabase()
      .then((db) => {
        _db = db;
        _openError = null;
        return db;
      })
      .catch((err) => {
        // Si la apertura falla, no dejamos la promesa rota cacheada: pasado el
        // plazo, el siguiente intento reabre desde cero.
        _dbPromise = null;
        _openFailedAt = Date.now();
        _openError = err;
        try { (err as { yaReportado?: boolean }).yaReportado = true; } catch { /* primitivo */ }
        throw err;
      });
  }
  return _dbPromise;
}

// ¿El error indica una conexión IDB muerta/cerrada? En ese caso reabrir y
// reintentar tiene sentido; para el resto (quota, datos, aborts de lógica)
// propagamos tal cual.
// Clasificación de los fallos de IndexedDB que merecen UN reintento:
//  - 'conexion': la conexión cacheada está muerta (el navegador la terminó,
//    típico al navegar a /login con un flush en curso). Hay que reabrir.
//  - 'transaccion': la transacción murió pero la conexión sigue viva. Pasa
//    cuando Android congela la pestaña (pantalla apagada) con una transacción
//    abierta: al despertar, el navegador la mata con TimeoutError "Transaction
//    timed out due to inactivity" o TransactionInactiveError. Caso real del 14
//    sep 2026 (Egipto): 20 minutos congelado en mitad de una subida de fotos.
//    Reintentar es seguro porque todas las operaciones de este módulo son
//    put/delete por clave o lecturas: repetirlas no duplica nada.
//  - null: cualquier otra cosa (cuota llena, datos corruptos, bug nuestro) se
//    propaga tal cual — reintentar no lo arreglaría y escondería el problema.
export type DbRetryKind = 'conexion' | 'transaccion' | null;
export function classifyDbError(err: unknown): DbRetryKind {
  const name = (err as { name?: unknown } | null | undefined)?.name;
  if (typeof name !== 'string') return null;
  if (isLostConnection(err)) return 'conexion';
  if (name === 'InvalidStateError' || name === 'NotFoundError') return 'conexion';
  if (name === 'TimeoutError' || name === 'TransactionInactiveError') return 'transaccion';
  return null;
}

// Ejecuta una operación contra la BD reintentándola UNA vez si el fallo es de
// los que se curan con un segundo intento (ver classifyDbError). Con la
// conexión muerta se reabre antes de reintentar; con la transacción muerta se
// reutiliza la conexión. Exportada solo para poder probar el reintento.
export async function withDB<T>(op: (db: IDBPDatabase<AldahraDB>) => Promise<T>): Promise<T> {
  try {
    return await op(await getDB());
  } catch (err) {
    if (isStorageBroken(err)) {
      markStorageUnavailable(err);
      throw err;
    }
    const kind = classifyDbError(err);
    if (kind === null) throw err;
    if (kind === 'conexion') {
      // Cerrar la conexión vieja antes de soltarla: en WebKit una conexión
      // perdida sigue "abierta" y la reapertura podría reutilizarla.
      try { _db?.close(); } catch { /* ya cerrada */ }
      resetDB();
    }
    try {
      return await op(await getDB());
    } catch (err2) {
      // Si ni reabriendo se recupera, el almacenamiento está inservible por
      // ahora: un único reporte y espera de OPEN_RETRY_MS (ver abajo).
      if (isStorageBroken(err2) || isLostConnection(err2)) markStorageUnavailable(err2);
      throw err2;
    }
  }
}

// Almacenamiento roto a nivel de fichero. WebKit (iPhone, iOS 18.7, 21 sep
// 2026) abre la base pero cualquier lectura falla con UnknownError: "Unable to
// open database file on disk" o "Error looking up record in object store by
// key range" (Sentry PWA-15/16). No es un fallo de una transacción que se cure
// reintentando ni un bug nuestro: el fichero SQLite de ese sitio no se puede
// usar hasta que el usuario borre los datos del sitio o reinstale. Mientras
// tanto se trata igual que una apertura fallida: se descarta la conexión y
// durante OPEN_RETRY_MS todo el módulo rechaza con el MISMO error, marcado
// como ya reportado, así Sentry recibe un evento y no uno por lectura.
export function isStorageBroken(err: unknown): boolean {
  return (err as { name?: unknown } | null | undefined)?.name === 'UnknownError' && !isLostConnection(err);
}

// Conexión perdida de WebKit, distinta del almacenamiento roto: al volver de
// segundo plano, iOS deja la conexión con el servidor de IndexedDB inservible
// y toda lectura falla con UnknownError "Attempt to get records from database
// without an in-progress transaction" o "Connection to Indexed Database server
// lost". Reabrir la base lo cura. Caso real (1 oct 2026, iPhone iOS 18.7,
// mohamed@): se trataba como almacenamiento roto, la sincronización se
// cortaba sin reintentar y la pantalla de detalle soltaba promesas sin capturar.
const LOST_CONNECTION_RE = /in-progress transaction|indexed\s?db server lost|indexed database server lost|database connection is closing/i;
export function isLostConnection(err: unknown): boolean {
  const e = err as { name?: unknown; message?: unknown } | null | undefined;
  return e?.name === 'UnknownError' && typeof e.message === 'string' && LOST_CONNECTION_RE.test(e.message);
}

function markStorageUnavailable(err: unknown): void {
  _openFailedAt = Date.now();
  _openError = err;
  try { (err as { yaReportado?: boolean }).yaReportado = true; } catch { /* primitivo */ }
  try { _db?.close(); } catch { /* ya cerrada */ }
  resetDB();
}

// Lectura en una transacción explícita cuyo `done` queda observado. Con los
// atajos de idb (db.get / getAll / count / getFromIndex) la promesa `done` de
// la transacción se crea sin nadie que la espere: si el navegador aborta la
// transacción (almacenamiento roto, ver arriba), el request falla con su error
// —que sí manejamos— Y `done` rechaza con un AbortError que no captura nadie:
// "AbortError: AbortError" como promesa no manejada en Sentry (PWA-4, 21 sep
// 2026), dos por cada lectura fallida. Las escrituras no lo sufren porque idb
// sí espera su `done`.
export async function readStore<Name extends StoreNames<AldahraDB>, T>(
  db: IDBPDatabase<AldahraDB>,
  storeName: Name,
  read: (store: IDBPObjectStore<AldahraDB, [Name], Name, 'readonly'>) => Promise<T>,
): Promise<T> {
  const tx = db.transaction(storeName, 'readonly');
  tx.done.catch(() => { /* el error real ya viaja con el request */ });
  return read(tx.store);
}

// ── Positions ────────────────────────────────────────────────────────────────
export async function cachePositions(date: string, data: Position[]) {
  await withDB((db) => db.put('positions', { date, data }));
}

// Busca una Position cacheada por idCargo sin saber la fecha. Es la red de
// seguridad de los ids maestros (idProduct/idCarrier/idPlanning) cuando la
// pantalla se abre sin ellos: p. ej. al relanzar la app instalada sobre
// /detail/<id>, sin history.state ni ?date=. Sin esos ids, un detalle NUEVO
// reventaba en el servidor con FK_CargoDetail_Product y quedaba atascado en
// la cola para siempre (Egipto, sep 2026; PWA-8 desde mayo).
export async function findCachedPositionByCargo(idCargo: number): Promise<Position | null> {
  if (!Number.isFinite(idCargo) || idCargo <= 0) return null;
  const records = await withDB((db) => readStore(db, 'positions', (s) => s.getAll()));
  for (const rec of records) {
    if (!Array.isArray(rec?.data)) continue;
    const hit = rec.data.find((p) => p && p.idCargo === idCargo);
    if (hit) return hit;
  }
  return null;
}

export async function getCachedPositions(date: string): Promise<Position[] | null> {
  const record = await withDB((db) => readStore(db, 'positions', (s) => s.get(date)));
  // Solo un array vale como lista: si alguna vez se cacheó otra cosa (una
  // respuesta rara de la API), devolver null obliga a ir al servidor en vez
  // de reventar la pantalla con "positions.some is not a function".
  return Array.isArray(record?.data) ? record.data : null;
}

// Limpia las cachés de VISUALIZACIÓN (por usuario/fecha). Se usa al cambiar de
// usuario para que no se muestren datos del anterior. NO toca la cola offline
// (pendingDetails), los borradores de fotos ni los geocodes.
export async function clearDisplayCaches(): Promise<void> {
  await withDB((db) => Promise.all([db.clear('positions'), db.clear('cargoDetails')]).then(() => undefined));
}

// ── Cargo details ─────────────────────────────────────────────────────────────
export async function cacheCargoDetails(idCargo: number, data: Detail[]) {
  await withDB((db) => db.put('cargoDetails', { idCargo, data, cachedAt: Date.now() }));
}

export async function getCachedCargoDetails(idCargo: number): Promise<Detail[] | null> {
  const record = await withDB((db) => readStore(db, 'cargoDetails', (s) => s.get(idCargo)));
  return record?.data ?? null;
}

// Mutador de la caché de details: aplica un updater al listado ya cacheado.
// No-op si la caché aún no existe — se rellenará en el próximo fetch
// online. Si existe, se sobreescribe con el resultado del updater.
// Lo usan las pantallas tras CREATE/EDIT/DELETE online para que la vista
// offline (que cae al cache) no muestre datos fantasmas.
export async function updateCachedCargoDetails(
  idCargo: number,
  updater: (list: Detail[]) => Detail[],
): Promise<void> {
  const current = await getCachedCargoDetails(idCargo);
  if (current == null) return;
  const next = updater(current);
  await cacheCargoDetails(idCargo, next);
}

// Mutador equivalente para la caché de positions (por fecha).
export async function updateCachedPositions(
  date: string,
  updater: (list: Position[]) => Position[],
): Promise<void> {
  const current = await getCachedPositions(date);
  if (current == null) return;
  const next = updater(current);
  await cachePositions(date, next);
}

// ── Factory geocodes ─────────────────────────────────────────────────────────
//
// Cache persistente: factoryName normalizado (lowercase + trim) → coords.
// Tanto hits (lat/lng) como misses (lat/lng = null con TTL gestionado en el
// servicio de geocoding) se persisten para no martillear el geocoder.
export interface FactoryGeocode {
  factoryName: string;
  lat: number | null;
  lng: number | null;
  country?: string | null;
  geocodedAt: number;
}

function normalizeFactoryKey(name: string): string {
  return (name ?? '').trim().toLowerCase();
}

export async function getCachedFactoryGeocode(factoryName: string): Promise<FactoryGeocode | null> {
  if (!factoryName) return null;
  const rec = await withDB((db) => readStore(db, 'factoryGeocodes', (s) => s.get(normalizeFactoryKey(factoryName))));
  return rec ?? null;
}

export async function putFactoryGeocode(rec: FactoryGeocode): Promise<void> {
  if (!rec.factoryName) return;
  await withDB((db) =>
    db.put('factoryGeocodes', { ...rec, factoryName: normalizeFactoryKey(rec.factoryName) })
  );
}

export async function deleteFactoryGeocode(factoryName: string): Promise<void> {
  if (!factoryName) return;
  await withDB((db) => db.delete('factoryGeocodes', normalizeFactoryKey(factoryName)));
}

export async function clearAllFactoryGeocodes(): Promise<void> {
  await withDB((db) => db.clear('factoryGeocodes'));
}

// ── Auth token (para acceso desde el Service Worker) ─────────────────────────
export async function setTokenInDB(token: string) {
  await withDB((db) => db.put('authToken', token, 'current'));
}

export async function clearTokenFromDB() {
  await withDB((db) => db.delete('authToken', 'current'));
}

// ── Pending details ───────────────────────────────────────────────────────────

// Encola una operación pendiente con deduplicación.
// Replica el comportamiento de BBDDOfflineRepository.upsertWithoutDuplicates en
// la app Android: si ya existe un pendiente para el mismo detalle, se mezcla en
// lugar de duplicarse — así el usuario que edita 3 veces el mismo registro
// offline solo encola una operación final.
export async function addPendingDetail(
  idCargo: number,
  operateType: OperateType,
  detail: Detail
): Promise<number> {
 return withDB(async (db) => {
  const tx = db.transaction('pendingDetails', 'readwrite');
  const store = tx.objectStore('pendingDetails');

  const detailId = detail.idCargoDetail ?? 0;
  let existing: PendingDetail | undefined;

  if (operateType !== 'NEW' && detailId > 0) {
    // EDIT/DELETE sobre un detalle existente: buscamos pendiente previo.
    existing = await store.index('by-detail').get(detailId);
  } else if (operateType === 'NEW') {
    // NEW antes de tener idCargoDetail real. Deduplicamos por número de
    // contenedor + cargo si está disponible (evita duplicados al pulsar dos
    // veces "Guardar" en modo offline).
    if (detail.containerNumber) {
      const all = await store.index('by-cargo').getAll(idCargo);
      existing = all.find((p) =>
        p.operateType === 'NEW' &&
        (p.detail.containerNumber ?? '').toUpperCase() === detail.containerNumber.toUpperCase()
      );
    }
  }

  let id: number;
  if (existing) {
    // Si pasamos de EDIT a DELETE, gana el DELETE.
    // Si NEW pendiente recibe EDIT, sigue como NEW (con los nuevos campos).
    // Si NEW pendiente recibe DELETE, se elimina directamente.
    if (existing.operateType === 'NEW' && operateType === 'DELETE') {
      await store.delete(existing.id);
      await tx.done;
      return existing.id;
    }
    const nextOp: OperateType = existing.operateType === 'NEW' ? 'NEW' : operateType;
    const finalId = detailId || existing.idCargoDetail;
    const merged: PendingDetail = {
      ...existing,
      idCargo,
      idCargoDetail: finalId,
      operateType: nextOp,
      detail: { ...detail, idCargoDetail: finalId },
      // No reseteamos retries ni createdAt — la operación sigue siendo "la misma".
      // Pero los DATOS sí cambiaron: deben volver a viajar en la pasada 1.
      dataSyncedAt: undefined,
      // Una edición fresca del usuario revive un item marcado como
      // destino-inexistente: quizá el detalle reapareció y por eso vuelve a
      // tocarlo. Deja que se reintente en lugar de seguir aparcado.
      targetGoneAt: undefined,
    };
    // Fotos pendientes del item previo: NO se pueden perder porque el nuevo
    // guardado venga sin hablar de fotos. Pasa en la edición INLINE de la lista
    // de detalles, que encola `{...current, ...fields}` con el detalle tal cual
    // vino del servidor — sin `listNewImages`. Sustituir el detail entero
    // borraba en silencio las fotos que esperaban en cola.
    // Solo se conserva cuando el campo llega SIN INFORMAR (undefined): un array
    // vacío es un vaciado explícito de la pantalla de edición y debe respetarse.
    // Y solo si el item sigue siendo NEW/EDIT — en un DELETE las fotos ya no
    // tienen destino.
    if (nextOp !== 'DELETE' && detail.listNewImages === undefined) {
      const prevPhotos = existing.detail?.listNewImages;
      if (Array.isArray(prevPhotos) && prevPhotos.length > 0) {
        merged.detail.listNewImages = prevPhotos;
      }
    }
    // listDeleteImages es un COMANDO acumulativo, no un dato: si el pending
    // previo tenía borrados AÚN SIN APLICAR y el guardado nuevo llega sin
    // ellos (reapertura offline: el GET de fotos de servidor no cargó y la
    // pantalla arranca sin lista de borrados), reemplazar los perdería en
    // silencio. Unión con dedup por imageName (el GUID del servidor).
    type DelEntry = { imageName?: string };
    const prevDel = (existing.detail?.listDeleteImages ?? []) as DelEntry[];
    if (prevDel.length > 0) {
      const newDel = (detail.listDeleteImages ?? []) as DelEntry[];
      const have = new Set(newDel.map((p) => (p.imageName ?? '').trim().toLowerCase()));
      merged.detail.listDeleteImages = [
        ...newDel,
        ...prevDel.filter((p) => !have.has((p.imageName ?? '').trim().toLowerCase())),
      ] as Detail['listDeleteImages'];
    }
    await store.put(merged);
    id = existing.id;
  } else {
    // CRÍTICO: garantizamos que detail.idCargoDetail queda sincronizado con
    // detailId. Si llamadores pasan un detail sin idCargoDetail (o con un
    // valor diferente del raíz), el flush del DELETE acabaría enviando
    // `undefined` en la URL — el backend lo interpreta como null/0 y dispara
    // 404, atascando el item con reintentos infinitos.
    const item: Omit<PendingDetail, 'id'> & { id?: number } = {
      idCargo,
      idCargoDetail: detailId,
      operateType,
      detail: { ...detail, idCargoDetail: detailId },
      retries: 0,
      createdAt: Date.now(),
    };
    id = (await store.add(item as PendingDetail)) as number;
  }
  await tx.done;
  // Antes registrábamos aquí Background Sync directamente, pero eso saltaba
  // la guarda de "manual offline". Ahora son los callers los que llaman a
  // notifyPendingChanged(), que registra el sync solo cuando procede.
  return id;
 });
}

export async function getPendingDetails(): Promise<PendingDetail[]> {
  return withDB((db) => readStore(db, 'pendingDetails', (s) => s.getAll()));
}

export async function getPendingById(id: number): Promise<PendingDetail | undefined> {
  return withDB((db) => readStore(db, 'pendingDetails', (s) => s.get(id)));
}

// Persiste un item completo en la cola — usado por el sincronizador del
// cliente para guardar progreso intermedio (fotos pendientes restantes,
// retries, etc.). Distinto de addPendingDetail: no aplica deduplicación.
export async function putPendingItem(item: PendingDetail): Promise<void> {
  await withDB((db) => db.put('pendingDetails', item));
}

// Resetea retries y lastAttemptAt de todos los pendientes — botón "forzar
// subida". Da una nueva oportunidad a items que excedieron el tope de
// reintentos, y también a los aparcados por destino inexistente.
export async function resetAllPendingRetries(): Promise<void> {
  await withDB(async (db) => {
    const tx = db.transaction('pendingDetails', 'readwrite');
    const store = tx.objectStore('pendingDetails');
    let cursor = await store.openCursor();
    while (cursor) {
      const value = cursor.value;
      if ((value.retries ?? 0) > 0 || value.nextAttemptAt != null || value.targetGoneAt != null) {
        value.retries = 0;
        value.lastAttemptAt = undefined;
        // Sin limpiar el backoff, el reset no reanuda nada: el item seguiría
        // saltándose hasta que venciera su nextAttemptAt, y el botón parecería
        // no hacer nada justo cuando más falta hace.
        value.nextAttemptAt = undefined;
        // "Forzar subida" también reanima items aparcados por destino
        // inexistente — por si el detalle reapareció en el portal.
        value.targetGoneAt = undefined;
        await cursor.update(value);
      }
      cursor = await cursor.continue();
    }
    await tx.done;
  });
}

// Actualiza los campos de un item pendiente (NEW/EDIT) en la cola sin pasar
// por la API. Lo usan tanto la edición inline de DetailPage como la pantalla
// EditDetail cuando el item aún no se ha sincronizado al servidor.
// Espejo de BBDDOfflineRepository.updateOffline (Android), que persiste los
// cambios en ObjectBox sin tocar Retrofit.
export async function updatePendingDetailFields(
  id: number,
  detailUpdate: Partial<Detail>
): Promise<PendingDetail | null> {
  return withDB(async (db) => {
    const item = await readStore(db, 'pendingDetails', (s) => s.get(id));
    if (!item) return null;
    // Conservamos idCargoDetail del item (no permitimos que un update lo cambie:
    // identifica la entrada en cola y, para NEWs pendientes, vale 0 hasta sync).
    const { idCargoDetail: _ignoredId, ...rest } = detailUpdate;
    void _ignoredId;
    item.detail = { ...item.detail, ...rest };
    // El usuario cambió los datos: deben volver a viajar en la pasada 1.
    item.dataSyncedAt = undefined;
    await db.put('pendingDetails', item);
    return item;
  });
}

export async function getPendingDetailsByCargo(idCargo: number): Promise<PendingDetail[]> {
  return withDB((db) => readStore(db, 'pendingDetails', (s) => s.index('by-cargo').getAll(idCargo)));
}

// Devuelve el pending (EDIT/DELETE) asociado a un detalle existente del
// servidor. Las pantallas VER/EDIT lo usan para fusionar las fotos locales
// añadidas offline con las del servidor mientras la sync no ha corrido.
export async function getPendingDetailByDetailId(idCargoDetail: number): Promise<PendingDetail | undefined> {
  if (!idCargoDetail || idCargoDetail <= 0) return undefined;
  return withDB((db) => readStore(db, 'pendingDetails', (s) => s.index('by-detail').get(idCargoDetail)));
}

export async function getPendingCount(): Promise<number> {
  return withDB((db) => readStore(db, 'pendingDetails', (s) => s.count()));
}

export async function deletePendingDetail(id: number) {
  await withDB((db) => db.delete('pendingDetails', id));
}

// Convierte un NEW pendiente a EDIT con el ID real del servidor para evitar
// duplicados si la subida de fotos falla tras crear el detalle.
export async function updatePendingDetailToEdit(id: number, realDetailId: number) {
  let idCargoForDraft: number | null = null;
  await withDB(async (db) => {
    const item = await readStore(db, 'pendingDetails', (s) => s.get(id));
    if (!item) return;
    item.operateType = 'EDIT';
    item.idCargoDetail = realDetailId;
    item.detail.idCargoDetail = realDetailId;
    // El create acaba de entregar los DATOS: solo quedan fotos.
    item.dataSyncedAt = Date.now();
    item.serverRejects = undefined;
    item.serverRejectMsg = undefined;
    await db.put('pendingDetails', item);
    idCargoForDraft = item.idCargo;
  });
  // El item cambió de identidad: el borrador de fotos que la pantalla guardó
  // bajo la clave del pending debe seguirle (si no, quedaría huérfano y el TTL
  // lo purgaría — pérdida silenciosa).
  if (idCargoForDraft != null) {
    await renamePhotoDraft(`${idCargoForDraft}:p${id}`, `${idCargoForDraft}:${realDetailId}`);
  }
}

export async function clearPendingDetails() {
  await withDB((db) => db.clear('pendingDetails'));
}

// ── Borradores de fotos (persistencia AL CAPTURAR) ──────────────────────────
// Las fotos añadidas en la pantalla de edición se persisten aquí en el momento
// de añadirlas, con clave estable por carga/detalle. Si la pestaña muere sin
// guardar, al reabrir esa misma carga reaparecen. El borrador se limpia SOLO
// cuando cada foto está subida-confirmada o persistida en la cola offline
// (jamás antes — un crash entre limpiar y persistir fue pérdida real en
// Android).
export interface PhotoDraft {
  key: string;       // idCargo:idCargoDetail | idCargo:new | idCargo:p<pendingId>
  idCargo: number;
  photos: Detail['listNewImages'];
  updatedAt: number;
}

const PHOTO_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export async function getPhotoDraft(key: string): Promise<PhotoDraft | null> {
  const rec = await withDB((db) => readStore(db, 'photoDrafts', (s) => s.get(key)));
  return rec ?? null;
}

// Persiste el borrador; una lista vacía lo elimina (quitar la última foto
// también es una mutación que debe reflejarse).
export async function putPhotoDraft(
  key: string,
  idCargo: number,
  photos: PhotoDraft['photos'],
): Promise<void> {
  if (!Array.isArray(photos) || photos.length === 0) {
    await withDB((db) => db.delete('photoDrafts', key));
    return;
  }
  await withDB((db) => db.put('photoDrafts', { key, idCargo, photos, updatedAt: Date.now() }));
}

export async function deletePhotoDraft(key: string): Promise<void> {
  await withDB((db) => db.delete('photoDrafts', key));
}

function normDraftPhotoName(name?: string): string {
  return (name ?? '').trim().toLowerCase();
}

// Renombra un borrador a otra clave — necesario cuando la promoción NEW→EDIT
// cambia la identidad del item (idCargo:p<pendingId> → idCargo:idCargoDetail):
// sin esto, el borrador queda huérfano bajo la clave vieja, la reapertura no lo
// encuentra y el TTL lo purga a los 7 días = pérdida silenciosa. Si la clave
// destino ya existe, merge PRO-RECUPERACIÓN (unión con dedup por nombre —
// nunca se pisa nada).
export async function renamePhotoDraft(fromKey: string, toKey: string): Promise<void> {
  if (fromKey === toKey) return;
  try {
    await withDB(async (db) => {
      const tx = db.transaction('photoDrafts', 'readwrite');
      const store = tx.objectStore('photoDrafts');
      const from = await store.get(fromKey);
      if (!from) { await tx.done; return; }
      const to = await store.get(toKey);
      type DraftPhoto = { imageName?: string };
      let photos = (from.photos ?? []) as DraftPhoto[];
      if (to && Array.isArray(to.photos) && to.photos.length > 0) {
        const have = new Set((to.photos as DraftPhoto[]).map((p) => normDraftPhotoName(p.imageName)));
        const extras = photos.filter((p) => !have.has(normDraftPhotoName(p.imageName)));
        photos = [...(to.photos as DraftPhoto[]), ...extras];
      }
      if (photos.length > 0) {
        await store.put({
          key: toKey,
          idCargo: from.idCargo,
          photos: photos as PhotoDraft['photos'],
          updatedAt: Date.now(),
        });
      }
      await store.delete(fromKey);
      await tx.done;
    });
  } catch { /* best-effort: si falla, el huérfano lo purga el TTL */ }
}

// Purga de borradores con más de 7 días SIN tocar (updatedAt): cualquier
// mutación reciente renueva la vigencia, así que solo caen los realmente
// abandonados. Best-effort, una llamada por sesión desde el coordinador.
export async function purgeOldPhotoDrafts(): Promise<void> {
  try {
    await withDB(async (db) => {
      const tx = db.transaction('photoDrafts', 'readwrite');
      const store = tx.objectStore('photoDrafts');
      let cursor = await store.openCursor();
      const cutoff = Date.now() - PHOTO_DRAFT_TTL_MS;
      while (cursor) {
        if ((cursor.value.updatedAt ?? 0) < cutoff) {
          await cursor.delete();
        }
        cursor = await cursor.continue();
      }
      await tx.done;
    });
  } catch { /* best-effort */ }
}

// ── Lease de procesado por item ──────────────────────────────────────────────
// El claim es atómico porque get+check+put van en UNA transacción readwrite e
// IDB serializa las transacciones readwrite sobre la misma store: dos contextos
// no pueden adquirir el mismo item a la vez. TTL corto y renovable durante el
// procesado (cada foto subida renueva); si el dueño muere sin liberar, el lease
// caduca y el otro contexto lo roba.
export const PENDING_LEASE_TTL_MS = 3 * 60 * 1000;

// Adquiere el lease y devuelve el item FRESCO leído en la misma transacción
// (procesar un snapshot viejo re-subiría fotos que el otro contexto ya subió).
// null = lease ajeno vigente o item ya borrado.
export async function acquirePendingLease(id: number, owner: string): Promise<PendingDetail | null> {
  // Por withDB: tras volver de segundo plano en iOS el claim es lo primero que
  // toca la base y es justo donde aparece la conexión perdida.
  return withDB(async (db) => {
  const tx = db.transaction('pendingDetails', 'readwrite');
  const store = tx.objectStore('pendingDetails');
  const item = await store.get(id);
  if (!item) { await tx.done; return null; }
  const now = Date.now();
  if (item.leasedUntil != null && item.leasedUntil > now && item.leaseOwner !== owner) {
    await tx.done;
    return null;
  }
  item.leaseOwner = owner;
  item.leasedUntil = now + PENDING_LEASE_TTL_MS;
  await store.put(item);
  await tx.done;
  return item;
  });
}

// Renueva el lease. Devuelve si SEGUIMOS siendo el dueño — el caller DEBE
// comprobarlo y abortar el procesado del item si es false (otro contexto lo
// robó tras congelarnos >TTL; seguir escribiendo reabriría la carrera en
// versión lenta). Una excepción de IDB también devuelve false: si no podemos
// garantizar la propiedad, abortar es lo conservador.
export async function renewPendingLease(id: number, owner: string): Promise<boolean> {
  try {
    return await withDB(async (db) => {
      const tx = db.transaction('pendingDetails', 'readwrite');
      const store = tx.objectStore('pendingDetails');
      const item = await store.get(id);
      if (!item || item.leaseOwner !== owner) {
        await tx.done;
        return false;
      }
      item.leasedUntil = Date.now() + PENDING_LEASE_TTL_MS;
      await store.put(item);
      await tx.done;
      return true;
    });
  } catch {
    return false;
  }
}

// Libera el lease tras un fallo (el éxito borra el item y el lease muere con
// él). Liberar pronto evita que el otro contexto espere el TTL entero.
export async function releasePendingLease(id: number, owner: string): Promise<void> {
  try {
    await withDB(async (db) => {
      const tx = db.transaction('pendingDetails', 'readwrite');
      const store = tx.objectStore('pendingDetails');
      const item = await store.get(id);
      if (item && item.leaseOwner === owner) {
        item.leaseOwner = undefined;
        item.leasedUntil = undefined;
        await store.put(item);
      }
      await tx.done;
    });
  } catch { /* best-effort */ }
}

// ── Documento de transporte (DDT) pendiente ──────────────────────────────────
// Lo que el proveedor pidió hacer con el DDT de un contenedor sin conexión. Va
// en su propia store, sin tocar la cola de detalles y fotos: subir sustituye al
// anterior en el servidor, así que repetir es seguro y basta con guardar la
// ÚLTIMA petición por contenedor.
export interface QueuedDeliveryNote {
  idCargoDetail: number;
  idCargo: number;
  op: 'upload' | 'delete';
  // Solo en 'upload': el PDF como data URL (como las fotos de la cola: un
  // Blob en IndexedDB no es fiable en todos los Safari).
  dataUrl?: string;
  fileName?: string;
  queuedAt: number;
  attempts: number;
  nextAttemptAt?: number;
  // Rechazo DEFINITIVO del servidor (400/403/404): no se reintenta; la ficha
  // lo enseña para que el proveedor lo vuelva a subir o lo descarte.
  error?: string;
}

export async function getQueuedDeliveryNote(idCargoDetail: number): Promise<QueuedDeliveryNote | null> {
  const rec = await withDB((db) => readStore(db, 'deliveryNoteQueue', (s) => s.get(idCargoDetail)));
  return rec ?? null;
}

export async function getQueuedDeliveryNotes(): Promise<QueuedDeliveryNote[]> {
  return withDB((db) => readStore(db, 'deliveryNoteQueue', (s) => s.getAll()));
}

export async function putQueuedDeliveryNote(item: QueuedDeliveryNote): Promise<void> {
  await withDB((db) => db.put('deliveryNoteQueue', item));
}

export async function deleteQueuedDeliveryNote(idCargoDetail: number): Promise<void> {
  await withDB((db) => db.delete('deliveryNoteQueue', idCargoDetail));
}
